import {
  buildMarketUrl,
  getFullMarketContext,
  getMarketBootstrapContext,
  getMarketFromLocation,
  getResolvedMarketContext,
  getTargetForCountry,
  isExplicitMarketUrl,
  saveMarketContext
} from '@theme/market-context';

const USER_WRAPPER_SELECTOR = '.user-dropdown-wrapper';
const USER_BUTTON_SELECTOR = '#mobile-header-user-btn, .mobile-header__user-btn, #header-user-btn, .header-user-btn';
const USER_DROPDOWN_SELECTOR = '.user-dropdown';
const COUNTRY_NAMES = {
  AE: 'UAE',
  SA: 'KSA',
  KW: 'KWT',
  QA: 'QAT',
  OM: 'OMN',
  BH: 'BHR',
  US: 'USA',
  AU: 'AUS',
  CA: 'CAN',
  AM: 'Armenia',
  FR: 'France',
  DE: 'Germany',
  NL: 'Netherlands',
  GB: 'United Kingdom',
  IT: 'Italy',
  ES: 'Spain',
  BE: 'Belgium',
  CH: 'Switzerland',
  SE: 'Sweden',
  NO: 'Norway',
  BG: 'Bulgaria',
  TR: 'Turkey',
  EU: 'International'
};
const COUNTRY_SELECTOR_LABELS = {
  AE: 'UAE',
  SA: 'KSA',
  KW: 'KWT',
  QA: 'QAT',
  OM: 'OMN',
  BH: 'BHR',
  US: 'USA',
  AU: 'AUS',
  CA: 'CAN',
  DE: 'EU'
};
const ARABIC_SUPPORTED_COUNTRIES = ['AE', 'SA', 'QA', 'KW', 'OM', 'BH'];
const MARKET_SYNC_REDIRECT_KEY = 'mkt_redirect';
const LEGACY_MARKET_SYNC_REDIRECT_KEYS = [
  'market_sync_redirect_v1',
  'amprio_market_sync_redirect_v1'
];
const GEOIP_MARKET_PROMPT_DISMISSED_KEY = 'geoip_dismiss';
const LEGACY_GEOIP_MARKET_PROMPT_DISMISSED_KEYS = [
  'geoip_market_prompt_dismissed_v1',
  'amprio_geoip_market_prompt_dismissed_v1'
];
const GEOIP_ENDPOINT = '/apps/proxy/geoip';

function readSessionStorageItem(key, legacyKeys) {
  try {
    var value = sessionStorage.getItem(key);
    if (value != null) return value;
    var legacyList = Array.isArray(legacyKeys) ? legacyKeys : (legacyKeys ? [legacyKeys] : []);
    for (var i = 0; i < legacyList.length; i += 1) {
      value = sessionStorage.getItem(legacyList[i]);
      if (value == null) continue;
      sessionStorage.setItem(key, value);
      sessionStorage.removeItem(legacyList[i]);
      return value;
    }
    return null;
  } catch (_) {
    return null;
  }
}

function removeSessionStorageItems(key, legacyKeys) {
  try {
    if (key) sessionStorage.removeItem(key);
    var legacyList = Array.isArray(legacyKeys) ? legacyKeys : (legacyKeys ? [legacyKeys] : []);
    for (var i = 0; i < legacyList.length; i += 1) {
      sessionStorage.removeItem(legacyList[i]);
    }
  } catch (_) {}
}

let userDropdownEventsBound = false;
let countrySelectorInitialized = false;
let marketNavigationInFlight = false;
let langSwitchInFlight = false;

function isShopifyDesignMode() {
  return document.documentElement.classList.contains('shopify-design-mode') || Boolean(window.Shopify?.designMode);
}

function normalizeMarketUrl(url) {
  try {
    const parsed = new URL(String(url || '/'), window.location.origin);
    parsed.hash = '';
    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    parsed.pathname = path;
    return parsed.hostname + parsed.pathname + parsed.search;
  } catch (_) {
    return String(url || '');
  }
}

function isSameDocumentUrl(targetUrl) {
  return normalizeMarketUrl(targetUrl) === normalizeMarketUrl(window.location.href);
}

function getCurrentPageLocale() {
  const bootstrapLocale = String(
    (window.theme && window.theme.marketBootstrap && window.theme.marketBootstrap.locale) ||
      window.theme?.locale ||
      document.documentElement.lang ||
      ''
  ).toLowerCase();
  if (bootstrapLocale.indexOf('ar') === 0) return 'ar';

  const fromLocation = getMarketFromLocation(window.location.pathname);
  return String(fromLocation.locale || '').toLowerCase() === 'ar' ? 'ar' : 'en';
}

function canPerformMarketSyncRedirect(nextUrl) {
  if (isSameDocumentUrl(nextUrl)) return false;

  try {
    const raw = readSessionStorageItem(MARKET_SYNC_REDIRECT_KEY, LEGACY_MARKET_SYNC_REDIRECT_KEYS);
    if (!raw) return true;
    const entry = JSON.parse(raw);
    const age = Date.now() - Number(entry.at || 0);
    if (age > 15000) return true;

    const sameFrom = normalizeMarketUrl(entry.from) === normalizeMarketUrl(window.location.href);
    const sameTo = normalizeMarketUrl(entry.to) === normalizeMarketUrl(nextUrl);
    if (sameFrom && sameTo && Number(entry.count || 0) >= 1) return false;
    if (Number(entry.count || 0) >= 3 && age < 8000) return false;
  } catch (_) {}

  return true;
}

function markMarketSyncRedirect(nextUrl) {
  try {
    const previous = JSON.parse(readSessionStorageItem(MARKET_SYNC_REDIRECT_KEY, LEGACY_MARKET_SYNC_REDIRECT_KEYS) || '{}');
    const sameHop =
      normalizeMarketUrl(previous.from) === normalizeMarketUrl(window.location.href) &&
      normalizeMarketUrl(previous.to) === normalizeMarketUrl(nextUrl);
    sessionStorage.setItem(
      MARKET_SYNC_REDIRECT_KEY,
      JSON.stringify({
        from: window.location.href,
        to: nextUrl,
        at: Date.now(),
        count: sameHop ? Number(previous.count || 0) + 1 : 1
      })
    );
  } catch (_) {}
}

function clearMarketSyncRedirectGuard() {
  try {
    removeSessionStorageItems(MARKET_SYNC_REDIRECT_KEY, LEGACY_MARKET_SYNC_REDIRECT_KEYS);
  } catch (_) {}
}

function setMarketSwitchBusy(isBusy) {
  document.querySelectorAll('.lang-switch[data-lang-switch], [data-popup-trigger="delivery-location"]').forEach(function(el) {
    if (isBusy) {
      el.setAttribute('aria-busy', 'true');
      el.setAttribute('aria-disabled', 'true');
    } else {
      el.removeAttribute('aria-busy');
      el.removeAttribute('aria-disabled');
    }
  });
}

function normalizeCountryCode(value) {
  return String(value || '').trim().toUpperCase();
}

function getCurrentContext() {
  return getResolvedMarketContext();
}

function getCurrentCountryName(code) {
  const normalized = normalizeCountryCode(code);
  return COUNTRY_NAMES[normalized] || window.theme?.countryNames?.[normalized] || normalized || 'AE';
}

function getLocalizedCountryName(code) {
  const normalized = normalizeCountryCode(code);
  return window.theme?.countryNames?.[normalized] || getCurrentCountryName(normalized);
}

function interpolateMarketCopy(template, values) {
  return String(template || '').replace(/\[\[\s*(country|current_country|detected_country)\s*\]\]/g, function(_, key) {
    return values[key] || '';
  });
}

function decodeHtmlEntities(value) {
  const text = String(value || '');
  if (!text || text.indexOf('&') === -1) return text;

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

function getDeliveryLocationCopy(key) {
  const popup = typeof window.getDeliveryLocationPopup === 'function'
    ? window.getDeliveryLocationPopup()
    : document.getElementById('am-popup-delivery-location');
  return decodeHtmlEntities(popup?.dataset?.[key] || '');
}

function getGeoipPromptCopy(currentCode, detectedCode) {
  const detectedName = getLocalizedCountryName(detectedCode);
  const currentName = getLocalizedCountryName(currentCode);
  const baseValues = {
    country: detectedName,
    current_country: currentName,
    detected_country: detectedName
  };
  const currentValues = {
    ...baseValues,
    country: currentName
  };

  return {
    detectedName,
    currentName,
    title: interpolateMarketCopy(getDeliveryLocationCopy('geoipTitleTemplate'), baseValues),
    description: interpolateMarketCopy(getDeliveryLocationCopy('geoipDescriptionTemplate'), baseValues),
    submitLabel: interpolateMarketCopy(getDeliveryLocationCopy('geoipSubmitTemplate'), baseValues),
    cancelLabel: interpolateMarketCopy(getDeliveryLocationCopy('geoipCancelTemplate'), currentValues)
  };
}

function getCountrySelectorLabel(code) {
  const normalized = normalizeCountryCode(code);
  return COUNTRY_SELECTOR_LABELS[normalized] || normalized || 'AE';
}

function supportsArabicLanguage(countryCode) {
  return ARABIC_SUPPORTED_COUNTRIES.includes(normalizeCountryCode(countryCode));
}

function getShopifyCountryCode() {
  return normalizeCountryCode(window.theme?.shopifyCountry || window.theme?.defaultCountry || 'AE');
}

function getAvailableShopifyCountryCodes() {
  const codes = window.theme?.availableCountryCodes;
  if (!Array.isArray(codes)) return [];
  return codes.map(normalizeCountryCode).filter(Boolean);
}

function isManualMarketContext(context) {
  const locationSource = String(context?.locationSource || '').toLowerCase();
  const source = String(context?.source || '').toLowerCase();
  return locationSource === 'manual' || source === 'country_confirm';
}

function shouldPreserveManualMarket(stored, detectedCode) {
  const storedCode = normalizeCountryCode(stored?.countryCode || stored?.country || stored?.storeRegion);
  const nextCode = normalizeCountryCode(detectedCode);
  return Boolean(isManualMarketContext(stored) && storedCode && nextCode && storedCode !== nextCode);
}

function submitShopifyLocalization(countryCode, locale) {
  if (isShopifyDesignMode()) return false;

  const code = normalizeCountryCode(countryCode);
  const availableCodes = getAvailableShopifyCountryCodes();
  if (!code || !availableCodes.includes(code)) return false;

  const form = document.getElementById('delivery-location-localization-form');
  const countryInput = document.getElementById('delivery-location-localization-country');
  const languageInput = document.getElementById('delivery-location-localization-language');
  if (!(form instanceof HTMLFormElement) || !countryInput) return false;

  countryInput.value = code;
  if (languageInput) languageInput.value = String(locale || 'en').toLowerCase() === 'ar' ? 'ar' : 'en';
  HTMLFormElement.prototype.submit.call(form);
  return true;
}

function isSameTarget(a, b) {
  return String(a?.targetPrefix || '') === String(b?.targetPrefix || '');
}

function syncCountryDisplay() {
  const context = getCurrentContext();
  const label = getCountrySelectorLabel(getShopifyCountryCode() || context.countryCode || context.storeRegion);
  document.querySelectorAll('[data-country-display]').forEach(function(el) {
    el.textContent = label;
  });
}

function saveCurrentMarketContext(context, extra = {}) {
  const resolved = getTargetForCountry(context.countryCode || context.storeRegion || context.country || 'AE');
  return saveMarketContext({
    ...context,
    ...extra,
    countryCode: normalizeCountryCode(context.countryCode || context.storeRegion || context.country),
    country: normalizeCountryCode(context.countryCode || context.storeRegion || context.country).toLowerCase(),
    storeZone: context.storeZone || resolved.storeZone,
    storeRegion: context.storeRegion || resolved.storeRegion,
    targetPrefix: context.targetPrefix != null ? context.targetPrefix : resolved.targetPrefix,
    currency: context.currency || resolved.currency,
    locale: context.locale || 'en',
    updatedAt: new Date().toISOString()
  });
}

function navigateToMarketUrl(url) {
  const targetUrl = withCleanQueryAndHash(url);
  if (isSameDocumentUrl(targetUrl)) {
    clearMarketSyncRedirectGuard();
    return Promise.resolve();
  }
  if (marketNavigationInFlight) return Promise.resolve();
  marketNavigationInFlight = true;
  setMarketSwitchBusy(true);
  markMarketSyncRedirect(targetUrl);
  window.location.assign(targetUrl);
  return Promise.resolve();
}

function withCleanQueryAndHash(url) {
  const cleanSearch = sanitizeSearch(window.location.search);
  const hash = window.location.hash || '';
  try {
    const absolute = new URL(url, window.location.origin);
    absolute.search = cleanSearch;
    absolute.hash = hash;
    if (/^https?:\/\//i.test(String(url))) return absolute.toString();
    return absolute.pathname + absolute.search + absolute.hash;
  } catch (_) {
    return String(url || '/') + cleanSearch + hash;
  }
}

function sanitizeSearch(search) {
  const params = new URLSearchParams(search || '');
  params.delete('pb');
  const next = params.toString();
  return next ? '?' + next : '';
}

function stripPreviewBuilderParam() {
  try {
    if (!window.history || !window.history.replaceState) return;
    const current = new URL(window.location.href);
    if (!current.searchParams.has('pb')) return;
    current.searchParams.delete('pb');
    const nextSearch = current.searchParams.toString();
    const nextUrl = current.pathname + (nextSearch ? '?' + nextSearch : '') + current.hash;
    window.history.replaceState(window.history.state, '', nextUrl);
  } catch (error) {
    console.warn('market-navigation', error);
  }
}

function syncStoredMarketWithCurrentUrl() {
  const current = getMarketFromLocation(window.location.pathname);
  const bootstrap = getMarketBootstrapContext();
  const stored = getFullMarketContext() || {};
  const storedCode = normalizeCountryCode(stored.countryCode || stored.country || stored.storeRegion);
  const currentCode = getShopifyCountryCode() || normalizeCountryCode(current.countryCode);
  const currentTarget = getTargetForCountry(currentCode);
  const directUrl = isExplicitMarketUrl(window.location.pathname);
  const preserveManualSelection = isManualMarketContext(stored) && storedCode === currentCode;
  const keepStoredManualSelection = shouldPreserveManualMarket(stored, currentCode);

  if (directUrl) {
    const currentTarget = getTargetForCountry(currentCode);
    const storedTarget = storedCode ? getTargetForCountry(storedCode) : null;
    const preserveStoredCountry = currentCode === 'EU' && storedCode && storedTarget && isSameTarget(storedTarget, currentTarget);
    const nextCode = preserveStoredCountry ? storedCode : currentCode;
    const nextTarget = getTargetForCountry(nextCode);
    saveCurrentMarketContext({
      ...stored,
      ...bootstrap,
      ...current,
      ...nextTarget,
      countryCode: nextCode,
      country: nextCode.toLowerCase(),
      storeRegion: nextTarget.storeRegion,
      explicit: preserveStoredCountry ? Boolean(stored.explicit) : true,
      source: preserveStoredCountry ? (stored.source || 'stored') : 'url'
    });
    clearMarketSyncRedirectGuard();
    return;
  }

  if (keepStoredManualSelection) {
    const storedTarget = getTargetForCountry(storedCode);
    saveCurrentMarketContext({
      ...bootstrap,
      ...current,
      ...stored,
      ...storedTarget,
      countryCode: storedCode,
      country: storedCode.toLowerCase(),
      storeRegion: storedTarget.storeRegion,
      locale: stored.locale || getCurrentPageLocale(),
      locationSource: 'manual',
      explicit: true,
      source: stored.source || 'country_confirm'
    });
    clearMarketSyncRedirectGuard();
    return;
  }

  saveCurrentMarketContext({
    ...stored,
    ...bootstrap,
    ...current,
    ...currentTarget,
    countryCode: currentCode,
    country: currentCode.toLowerCase(),
    storeRegion: currentTarget.storeRegion,
    locale: getCurrentPageLocale(),
    locationSource: preserveManualSelection ? 'manual' : (current.locationSource || 'auto'),
    explicit: preserveManualSelection,
    source: preserveManualSelection ? 'country_confirm' : (bootstrap.source || current.source || 'bootstrap')
  });
  clearMarketSyncRedirectGuard();
}

function openDeliveryLocationPopup() {
  if (typeof window.openDeliveryLocationPopup === 'function') {
    window.openDeliveryLocationPopup();
  }
}

const GEOIP_DISMISSED_VALUE = '1';
let geoipPromptPending = false;

function isGeoipPromptDismissed() {
  try {
    return Boolean(readSessionStorageItem(GEOIP_MARKET_PROMPT_DISMISSED_KEY, LEGACY_GEOIP_MARKET_PROMPT_DISMISSED_KEYS));
  } catch (_) {
    return true;
  }
}

function dismissGeoipPrompt() {
  try {
    sessionStorage.setItem(GEOIP_MARKET_PROMPT_DISMISSED_KEY, GEOIP_DISMISSED_VALUE);
    removeSessionStorageItems('', LEGACY_GEOIP_MARKET_PROMPT_DISMISSED_KEYS);
  } catch (_) {}
  geoipPromptPending = false;
}

function initGeoipPromptDismissBridge() {
  if (window.__themeGeoipDismissBridgeBound) return;
  window.__themeGeoipDismissBridgeBound = true;
  document.addEventListener('popup:close', function(e) {
    if (!geoipPromptPending) return;
    const popup = e.target;
    if (!popup || popup.id !== 'am-popup-delivery-location') return;
    dismissGeoipPrompt();
  });
}

function normalizeGeoipPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const geo = payload.geo && typeof payload.geo === 'object' ? payload.geo : payload;
  const countryCode = normalizeCountryCode(geo.country_code || geo.countryCode || geo.country);
  if (!countryCode) return null;
  return {
    countryCode,
    countryName: geo.country_name || geo.countryName || geo.country || getCurrentCountryName(countryCode),
    city: geo.city || ''
  };
}

async function fetchGeoipMarket() {
  const controller = new AbortController();
  const timer = window.setTimeout(function() {
    controller.abort();
  }, 4000);

  try {
    const response = await fetch(GEOIP_ENDPOINT, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: controller.signal
    });
    if (!response.ok) return null;
    return normalizeGeoipPayload(await response.json());
  } catch (_) {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

async function runGeoipMarketPrompt() {
  if (isShopifyDesignMode()) return;
  if (isGeoipPromptDismissed()) return;

  const shopifyCode = getShopifyCountryCode();
  const stored = getFullMarketContext() || {};
  const storedCode = normalizeCountryCode(stored.countryCode || stored.country || stored.storeRegion);
  const activeCode = isManualMarketContext(stored) && storedCode ? storedCode : shopifyCode;
  const detected = await fetchGeoipMarket();
  const detectedCode = normalizeCountryCode(detected?.countryCode);
  if (!activeCode || !detectedCode || activeCode === detectedCode) return;
  if (isGeoipPromptDismissed()) return;

  const availableCodes = getAvailableShopifyCountryCodes();
  if (availableCodes.length && !availableCodes.includes(detectedCode)) return;

  const select = document.getElementById('delivery-location-country');
  if (select && !select.querySelector('option[value="' + detectedCode + '"]')) return;

  const shopifyTarget = getTargetForCountry(activeCode);
  const detectedTarget = getTargetForCountry(detectedCode);
  if (isSameTarget(shopifyTarget, detectedTarget)) return;

  const copy = getGeoipPromptCopy(activeCode, detectedCode);
  const opener = function() {
    if (typeof window.openDeliveryLocationPopup !== 'function') return false;
    window.openDeliveryLocationPopup({
      countryCode: detectedCode,
      language: supportsArabicLanguage(detectedCode) && getCurrentPageLocale() === 'ar' ? 'ar' : 'en',
      title: copy.title,
      description: copy.description,
      submitLabel: copy.submitLabel,
      cancelLabel: copy.cancelLabel
    });
    const popup = typeof window.getDeliveryLocationPopup === 'function'
      ? window.getDeliveryLocationPopup()
      : document.getElementById('am-popup-delivery-location');
    if (!popup || (popup.style.display !== 'flex' && !popup.classList.contains('am-popup--active'))) {
      return false;
    }
    geoipPromptPending = true;
    return true;
  };

  if (!opener()) {
    window.setTimeout(function retryOpenGeoipPrompt(attempt) {
      if (opener()) return;
      if (attempt >= 6) {
        dismissGeoipPrompt();
        return;
      }
      window.setTimeout(function() {
        retryOpenGeoipPrompt(attempt + 1);
      }, 250);
    }, 250, 0);
  }
}

function switchLanguage(nextLocale) {
  if (langSwitchInFlight || marketNavigationInFlight) return;

  const locale = String(nextLocale || 'en').toLowerCase() === 'ar' ? 'ar' : 'en';
  const current = getCurrentContext();
  const countryCode = normalizeCountryCode(current.countryCode || current.storeRegion || 'AE');
  const target = getTargetForCountry(countryCode);
  const resolvedLocale = supportsArabicLanguage(countryCode) ? locale : 'en';
  const currentLocale = getCurrentPageLocale();

  if (currentLocale === resolvedLocale) return;

  const nextUrl = buildMarketUrl(target, {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    preservePath: true,
    locale: resolvedLocale
  });

  if (isSameDocumentUrl(nextUrl)) {
    saveCurrentMarketContext({
      ...current,
      ...target,
      countryCode: countryCode,
      country: countryCode.toLowerCase(),
      locale: resolvedLocale,
      explicit: true,
      source: 'lang_switch'
    });
    return;
  }

  langSwitchInFlight = true;
  setMarketSwitchBusy(true);

  saveCurrentMarketContext({
    ...current,
    ...target,
    countryCode: countryCode,
    country: countryCode.toLowerCase(),
    locale: resolvedLocale,
    explicit: true,
    source: 'lang_switch'
  });

  if (typeof window.closeMobileMenuDrawer === 'function') {
    window.closeMobileMenuDrawer();
  }

  navigateToMarketUrl(nextUrl);
}

function initLangSwitch() {
  document.querySelectorAll('.lang-switch[data-lang-switch]').forEach(function(button) {
    if (button.__themeLangSwitchBound) return;
    button.__themeLangSwitchBound = true;

    button.addEventListener('click', function(e) {
      if (button.getAttribute('aria-disabled') === 'true') return;
      e.preventDefault();
      e.stopPropagation();
      switchLanguage(button.getAttribute('data-lang-switch'));
    });
  });
}

function closeDeliveryLocationPopup() {
  if (typeof window.closeDeliveryLocationPopup === 'function') {
    window.closeDeliveryLocationPopup();
  }
}

function initCountrySelector() {
  if (countrySelectorInitialized) return;
  const countryButtons = document.querySelectorAll('[data-popup-trigger="delivery-location"]');

  if (!countryButtons.length) return;
  countrySelectorInitialized = true;

  countryButtons.forEach(function(countryButton) {
    if (countryButton.classList.contains('lang-switch') && countryButton.hasAttribute('data-lang-switch')) {
      return;
    }

    countryButton.addEventListener('click', function(e) {
      if (countryButton.getAttribute('aria-disabled') === 'true') return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof window.closeMobileMenuDrawer === 'function') {
        window.closeMobileMenuDrawer();
      }
      openDeliveryLocationPopup();
    });
  });

  document.querySelectorAll('[data-country-selector]').forEach(function(wrapper) {
    wrapper.addEventListener('click', function(e) {
      if (e.target.closest('a, button')) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof window.closeMobileMenuDrawer === 'function') {
        window.closeMobileMenuDrawer();
      }
      openDeliveryLocationPopup();
    });
  });

  const confirmButton = document.getElementById('delivery-location-confirm-region');
  const availableCodes = getAvailableShopifyCountryCodes();
  const countrySelect = document.getElementById('delivery-location-country');
  if (countrySelect && availableCodes.length) {
    Array.from(countrySelect.options).forEach(function(option) {
      option.disabled = !availableCodes.includes(normalizeCountryCode(option.value));
    });
  }

  if (confirmButton) {
    confirmButton.addEventListener('click', function(e) {
      const countrySelect = document.getElementById('delivery-location-country');
      const languageSelect = document.getElementById('delivery-location-language');
      if (!countrySelect || !countrySelect.value || !languageSelect) return;

      e.preventDefault();
      e.stopPropagation();

      if (langSwitchInFlight || marketNavigationInFlight) return;

      const selectedCountry = normalizeCountryCode(countrySelect.value);
      const selectedLanguage = normalizeCountryCode(languageSelect.value) === 'AR' ? 'ar' : 'en';
      const current = getCurrentContext();
      const target = getTargetForCountry(selectedCountry);
      const nextLanguage = supportsArabicLanguage(selectedCountry) ? selectedLanguage : 'en';

      saveCurrentMarketContext({
        ...current,
        ...target,
        countryCode: selectedCountry,
        country: selectedCountry.toLowerCase(),
        locale: nextLanguage,
        explicit: true,
        locationSource: 'manual',
        source: 'country_confirm'
      });
      if (typeof window.closeDeliveryLocationPopup === 'function') {
        window.closeDeliveryLocationPopup();
      }

      if (selectedCountry === getShopifyCountryCode() && nextLanguage === getCurrentPageLocale()) {
        window.theme.shopifyCountry = selectedCountry;
        window.theme.country = selectedCountry;
        window.theme.currency = target.currency;
        document.dispatchEvent(new CustomEvent('theme:convert-prices'));
        syncCountryDisplay();
        return;
      }

      langSwitchInFlight = true;
      setMarketSwitchBusy(true);
      if (!submitShopifyLocalization(selectedCountry, nextLanguage)) {
        langSwitchInFlight = false;
        setMarketSwitchBusy(false);
        openDeliveryLocationPopup();
      }
    });
  }

}

function showComingSoonToast(feature) {
  if (typeof window.showSystemToast === 'function') {
    window.showSystemToast({
      title: `${feature} - Coming soon`,
      duration: 3000,
    });
    return;
  }
}

function closeUserDropdowns(exceptWrapper) {
  document.querySelectorAll(USER_WRAPPER_SELECTOR).forEach(function(wrapper) {
    if (exceptWrapper && wrapper === exceptWrapper) return;
    wrapper.removeAttribute('data-dropdown-open');
    const button = wrapper.querySelector(USER_BUTTON_SELECTOR);
    if (button) button.setAttribute('aria-expanded', 'false');
  });
}

function toggleUserDropdown(wrapper) {
  if (!wrapper) return;
  const isOpen = wrapper.getAttribute('data-dropdown-open') === 'true';
  if (isOpen) {
    closeUserDropdowns();
    return;
  }

  closeUserDropdowns(wrapper);
  wrapper.setAttribute('data-dropdown-open', 'true');
  const button = wrapper.querySelector(USER_BUTTON_SELECTOR);
  if (button) button.setAttribute('aria-expanded', 'true');
}

function initUserDropdowns() {
  document.querySelectorAll(USER_WRAPPER_SELECTOR).forEach(function(wrapper) {
    const button = wrapper.querySelector(USER_BUTTON_SELECTOR);
    const dropdown = wrapper.querySelector(USER_DROPDOWN_SELECTOR);
    if (!button || !dropdown) return;
    button.setAttribute('aria-haspopup', 'menu');
    button.setAttribute('aria-expanded', wrapper.getAttribute('data-dropdown-open') === 'true' ? 'true' : 'false');
  });

  if (userDropdownEventsBound) return;

  document.addEventListener('click', function(e) {
    const button = e.target.closest(USER_BUTTON_SELECTOR);
    if (button) {
      e.preventDefault();
      e.stopPropagation();
      toggleUserDropdown(button.closest(USER_WRAPPER_SELECTOR));
      return;
    }

    if (!e.target.closest(USER_WRAPPER_SELECTOR)) {
      closeUserDropdowns();
    }
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') closeUserDropdowns();
  });

  userDropdownEventsBound = true;
}

document.addEventListener('click', function(e) {
  const burger = e.target.closest('.btn_menu_burger');
  if (burger) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const drawer = document.getElementById('mobile-menu-drawer');
    if (drawer) {
      if (drawer.parentNode !== document.body) {
        document.body.appendChild(drawer);
      }

      if (typeof window.openMobileMenuDrawer === 'function') {
        window.openMobileMenuDrawer();
      } else {
        const scrollY = window.__themeDocumentScrollY ? window.__themeDocumentScrollY() : (window.pageYOffset || 0);
        document.body.style.top = '-' + scrollY + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.classList.add('menu-drawer-open');
        drawer.classList.remove('is-hidden');
        void drawer.offsetWidth;
        drawer.classList.add('is-open');
      }
    }

    return;
  }

  const comingSoonTrigger = e.target.closest('[data-coming-soon]');
  if (!comingSoonTrigger) return;

  e.preventDefault();
  showComingSoonToast(comingSoonTrigger.getAttribute('data-coming-soon'));
}, true);

stripPreviewBuilderParam();
initUserDropdowns();

function boot() {
  if (isShopifyDesignMode()) {
    initCountrySelector();
    initLangSwitch();
    syncCountryDisplay();
    return;
  }

  syncStoredMarketWithCurrentUrl();
  initCountrySelector();
  initLangSwitch();
  syncCountryDisplay();
  initGeoipPromptDismissBridge();
  scheduleGeoipPrompt();
}

function scheduleGeoipPrompt() {
  function run() {
    runGeoipMarketPrompt();
  }
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 4000 });
    return;
  }
  window.setTimeout(run, 2000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

document.addEventListener('shopify:section:load', function() {
  initUserDropdowns();
  initLangSwitch();
  syncCountryDisplay();
});
